import React from 'react';

// useState from React - typed for Emotion jsxImportSource compatibility
type UseState = <S>(initialState: S | (() => S)) => [S, (value: S | ((prev: S) => S)) => void];
const useState = (React as unknown as { useState: UseState }).useState;
import { ConfigProvider } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTheme } from '../../../theme/ThemeContext';
import useNotification from '../../../atoms/Notification/useNotification';
import Modal from '../../../atoms/Modal/Modal';
import Input from '../../../atoms/Input/Input';
import Button from '../../../atoms/Button/Button';
import Search from '../../../atoms/Icon/Search';
import X from '../../../atoms/Icon/X';
import Bookmark from '../../../atoms/Icon/Bookmark';
import Trash from '../../../atoms/Icon/Trash';
import EditDoc from '../../../atoms/Icon/EditDoc';
import Check from '../../../atoms/Icon/Check';
import XClose from '../../../atoms/Icon/XClose';
import QuestionCircleOutlined from '../../../atoms/Icon/QuestionCircleOutlined';
import CheckDone from '../../../atoms/Icon/CheckDone';
import { AgenticQuestionBankProps, AgenticQuestionCategory, AgenticQuestion } from '../AskAsperAgentic.types';

// Default categories for the Agentic Question Bank
const DEFAULT_AGENTIC_CATEGORIES: AgenticQuestionCategory[] = [
    {
        id: 'base-incremental',
        name: 'Base & incremental',
        displayName: 'Base & incremental',
        description: 'Analyze base and incremental sales performance across your portfolio.',
        questions: [
            {
                id: 'bi1',
                text: 'What is the base vs incremental sales split for top brands in the category in last 52 weeks?',
            },
            { id: 'bi2', text: 'How has the incremental contribution changed over time?' },
            { id: 'bi3', text: 'Which products have the highest base sales?' },
        ],
    },
    {
        id: 'bpl',
        name: 'BPL',
        displayName: 'BPL',
        description: 'Brand Price Ladder analysis and insights.',
        questions: [
            { id: 'bpl1', text: 'What is my base price elasticities across different retailers?' },
            { id: 'bpl2', text: 'How does my brand pricing compare to competitors?' },
        ],
    },
    {
        id: 'distribution',
        name: 'Distribution',
        displayName: 'Distribution',
        description: 'Track and analyze distribution metrics across channels.',
        questions: [
            { id: 'd1', text: 'What is the distribution of my Brands in Target vs Walmart vs Publix?' },
            { id: 'd2', text: 'Which retailers have the highest distribution coverage?' },
        ],
    },
    {
        id: 'finance',
        name: 'Finance',
        displayName: 'Finance',
        description: 'Financial performance and metrics analysis.',
        questions: [
            { id: 'f1', text: 'What is my Price Per Unit and Price Per EQ across retailers?' },
            { id: 'f2', text: 'How has revenue trended over the past year?' },
        ],
    },
    {
        id: 'gainers-drainers',
        name: 'Gainers & Drainers',
        displayName: 'Gainers & Drainers',
        description: 'Identify top performers and underperformers in your portfolio.',
        questions: [
            { id: 'gd1', text: 'Which products are gaining market share?' },
            { id: 'gd2', text: 'What are the top drainers in my portfolio?' },
        ],
    },
    {
        id: 'pic',
        name: 'PIC',
        displayName: 'PIC',
        description: 'Price Index and Competitive analysis.',
        questions: [{ id: 'pic1', text: 'How does my price index compare to competitors?' }],
    },
    {
        id: 'price-index',
        name: 'Price index',
        displayName: 'Price index',
        description: 'Price index trends and analysis.',
        questions: [{ id: 'pi1', text: 'What is the price index trend over time?' }],
    },
    {
        id: 'pricing',
        name: 'Pricing',
        displayName: 'Pricing',
        description: 'Pricing strategy and optimization insights.',
        questions: [{ id: 'pr1', text: 'How does my pricing compare across channels?' }],
    },
    {
        id: 'pricing-overview',
        name: 'Pricing Overview',
        displayName: 'Pricing Overview',
        description: 'Comprehensive pricing overview and analysis.',
        questions: [{ id: 'po1', text: 'Give me an overview of my pricing performance.' }],
    },
    {
        id: 'sales-performance',
        name: 'Sales & Performance',
        displayName: 'Sales & Performance',
        description: 'Overall sales and performance metrics.',
        questions: [
            { id: 'sp1', text: 'How did my sales perform this quarter?' },
            { id: 'sp2', text: 'What are the key performance indicators for my portfolio?' },
        ],
    },
];

// Helper function to render question text with grey styling for parts enclosed in <>
const renderQuestionText = (text: string): React.ReactNode => {
    const regex = /(<[^>]+>)/g;
    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, index) => {
                if (part.startsWith('<') && part.endsWith('>')) {
                    const content = part.slice(1, -1);
                    return (
                        <span key={index} style={{ color: '#8c8c8c' }}>
                            {content}
                        </span>
                    );
                }
                return <span key={index}>{part}</span>;
            })}
        </>
    ) as React.ReactNode;
};

/**
 * AgenticQuestionBank - Question Bank panel with vertical sidebar navigation
 * Part of the AskAsper Agentic Platform components
 *
 * Features:
 * - Vertical sidebar with category list
 * - Main content area with questions
 * - Search functionality
 * - Edit and delete for saved questions
 */
const AgenticQuestionBank = (props: AgenticQuestionBankProps): React.ReactNode => {
    const {
        categories = DEFAULT_AGENTIC_CATEGORIES,
        savedQuestions = [],
        onClose,
        onQuestionSelect,
        onEditSavedQuestion,
        onDeleteSavedQuestion,
        'data-test': dataTest,
        'data-test-id': dataTestId,
    } = props;

    const theme = useTheme();
    const { success: showSuccessNotification } = useNotification();
    const [searchValue, setSearchValue] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
    const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [modal, contextHolder] = Modal.useModal();

    // Get selected category info
    const getSelectedCategoryInfo = () => {
        if (selectedCategoryId === 'all') {
            return {
                name: 'All Questions',
                description: 'Browse all available questions across all categories.',
            };
        }
        if (selectedCategoryId === 'saved') {
            return {
                name: 'Saved Questions',
                description: 'Build and save your own questions to quickly revisit important or recurring analyses.',
            };
        }
        const category = categories.find((c) => c.id === selectedCategoryId);
        return {
            name: category?.displayName || category?.name || '',
            description: category?.description || '',
        };
    };

    // Filter questions based on search value
    const filterQuestionsBySearch = (questions: AgenticQuestion[]): AgenticQuestion[] => {
        if (!searchValue.trim()) {
            return questions;
        }
        const searchLower = searchValue.toLowerCase().trim();
        return questions.filter((question) => question.text.toLowerCase().includes(searchLower));
    };

    // Get questions to display based on selected category and search
    const getDisplayedQuestions = (): AgenticQuestion[] => {
        let questions: AgenticQuestion[] = [];

        if (selectedCategoryId === 'all') {
            // Combine all questions from all categories
            categories.forEach((category) => {
                questions = [...questions, ...category.questions];
            });
        } else if (selectedCategoryId === 'saved') {
            questions = savedQuestions;
        } else {
            const category = categories.find((c) => c.id === selectedCategoryId);
            questions = category?.questions || [];
        }

        return filterQuestionsBySearch(questions);
    };

    const handleQuestionClick = (question: AgenticQuestion) => {
        if (onQuestionSelect) {
            onQuestionSelect(question, selectedCategoryId);
        }
    };

    const handleEditClick = (e: { stopPropagation: () => void }, question: AgenticQuestion) => {
        e.stopPropagation();
        setEditingQuestionId(question.id);
        setEditValue(question.text);
    };

    const handleSaveEdit = (questionId: string) => {
        if (editValue.trim() && onEditSavedQuestion) {
            const question = savedQuestions.find((q) => q.id === questionId);
            if (question) {
                const questionAny = question as any;
                const questionData: { id?: string | number; text?: string; QuestionID?: number; Question?: string } = {
                    text: editValue.trim(),
                };

                if (questionAny.QuestionID !== undefined && questionAny.QuestionID !== null) {
                    questionData.QuestionID =
                        typeof questionAny.QuestionID === 'number'
                            ? questionAny.QuestionID
                            : parseInt(String(questionAny.QuestionID), 10);
                    questionData.Question = editValue.trim();
                } else {
                    questionData.id = questionId;
                    questionData.text = editValue.trim();
                }

                onEditSavedQuestion(questionData);
                showSuccessNotification({
                    message: 'Question updated',
                    description: 'Your saved question has been updated successfully.',
                    placement: 'bottomRight',
                });
            }
        }
        setEditingQuestionId(null);
        setEditValue('');
    };

    const handleCancelEdit = () => {
        setEditingQuestionId(null);
        setEditValue('');
    };

    const handleDeleteClick = (e: { stopPropagation: () => void }, questionId: string) => {
        e.stopPropagation();
        modal.confirm({
            title: 'Do you want to delete this question?',
            icon: <ExclamationCircleOutlined style={{ color: '#ca3d3d' }} /> as React.ReactNode,
            content: 'Are you sure you want to permanently delete this question? This action cannot be undone',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            centered: true,
            onOk: () => {
                if (onDeleteSavedQuestion) {
                    onDeleteSavedQuestion(questionId);
                    showSuccessNotification({
                        message: 'Question deleted',
                        description: 'Your saved question has been deleted successfully.',
                        placement: 'bottomRight',
                    });
                }
            },
        });
    };

    const selectedInfo = getSelectedCategoryInfo();
    const displayedQuestions = getDisplayedQuestions();

    return (
        <ConfigProvider theme={theme}>
            {contextHolder}
            <div
                className="flex items-start justify-between w-full h-full bg-white pt-2"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-question-bank
            >
                {/* Left Sidebar */}
                <div className="w-[248px] shrink-0 border-r border-[#f0f0f0] flex flex-col gap-4 py-4 h-full">
                    {/* Title */}
                    <div className="px-6">
                        <h2 className="font-['Open_Sans'] font-bold text-[20px] leading-[28px] text-[rgba(0,0,0,0.88)] m-0">
                            Question Bank
                        </h2>
                    </div>

                    {/* Divider */}
                    <div className="w-full h-px bg-[#f0f0f0]" />

                    {/* Category List */}
                    <div className="px-6 flex flex-col gap-2 overflow-y-auto flex-1">
                        {/* All Questions */}
                        <div
                            className={`flex items-center gap-2 px-2 py-[7px] rounded-lg cursor-pointer transition-colors ${
                                selectedCategoryId === 'all'
                                    ? 'bg-[rgba(0,0,0,0.06)]'
                                    : 'bg-white hover:bg-[#FBFAFF]'
                            }`}
                            onClick={() => setSelectedCategoryId('all')}
                            data-test-id={`${dataTestId}-category-all`}
                        >
                            <div className="w-6 h-6 flex items-center justify-center border border-[#f0f0f0] rounded bg-white">
                                <CheckDone size={16} color="#4CBC38" />
                            </div>
                            <span className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                                All Questions
                            </span>
                        </div>

                        {/* Saved Questions */}
                        <div
                            className={`flex items-center gap-2 px-2 py-[7px] rounded-lg cursor-pointer transition-colors ${
                                selectedCategoryId === 'saved'
                                    ? 'bg-[rgba(0,0,0,0.06)]'
                                    : 'bg-white hover:bg-[#FBFAFF]'
                            }`}
                            onClick={() => setSelectedCategoryId('saved')}
                            data-test-id={`${dataTestId}-category-saved`}
                        >
                            <div className="w-6 h-6 flex items-center justify-center border border-[#f0f0f0] rounded bg-white">
                                <Bookmark size={14} color="#DD449F" />
                            </div>
                            <span className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                                Saved Questions
                            </span>
                        </div>

                        {/* Dynamic Categories */}
                        {categories.map((category) => (
                            <div
                                key={category.id}
                                className={`flex items-center gap-2 px-2 py-[7px] rounded-lg cursor-pointer transition-colors ${
                                    selectedCategoryId === category.id
                                        ? 'bg-[rgba(0,0,0,0.06)]'
                                        : 'bg-white hover:bg-[#FBFAFF]'
                                }`}
                                onClick={() => setSelectedCategoryId(category.id)}
                                data-test-id={`${dataTestId}-category-${category.id}`}
                            >
                                <div className="w-6 h-6 flex items-center justify-center border border-[#f0f0f0] rounded">
                                    <QuestionCircleOutlined size={14} />
                                </div>
                                <span className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                                    {category.displayName || category.name}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Content Area */}
                <div className="flex-1 flex flex-col gap-6 items-center py-1.5 h-full overflow-hidden">
                    {/* Header */}
                    <div className="w-full flex flex-col gap-3">
                        <div className="flex items-center justify-between px-6">
                            <div className="flex flex-col">
                                <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-[#18181b] m-0">
                                    {selectedInfo.name}
                                </h3>
                                <p className="font-['Open_Sans'] text-sm leading-[18px] text-[#71717b] m-0">
                                    {selectedInfo.description}
                                </p>
                            </div>
                            <div
                                className="w-[22px] h-[22px] flex items-center justify-center cursor-pointer rounded hover:bg-gray-100"
                                onClick={onClose}
                                data-test-id={`${dataTestId}-close`}
                            >
                                <X size={16} />
                            </div>
                        </div>
                        <div className="w-full h-px bg-[#f0f0f0]" />
                    </div>

                    {/* Search and Questions */}
                    <div className="w-full flex flex-col gap-5 px-6 flex-1 overflow-hidden">
                        {/* Search Input */}
                        <div className="flex items-stretch w-full border border-[#d9d9d9] rounded-lg overflow-hidden">
                            <Input
                                placeholder="Search"
                                value={searchValue}
                                onChange={(e: { target: { value: string } }) => setSearchValue(e.target.value)}
                                className="flex-1 border-0 rounded-none text-[14px] shadow-none focus:shadow-none h-[40px]"
                                data-test-id={`${dataTestId}-search-input`}
                            />
                            <Button
                                icon={<Search size={20} />}
                                className="border-0 border-l border-[#d9d9d9] rounded-none h-[40px] w-[40px] p-0 flex items-center justify-center"
                                data-test-id={`${dataTestId}-search-button`}
                            />
                        </div>

                        {/* Questions List */}
                        <div className="flex flex-col gap-1 overflow-y-auto flex-1">
                            {displayedQuestions.length === 0 ? (
                                <div className="text-center py-12 text-[rgba(0,0,0,0.45)]">
                                    <p className="font-['Open_Sans'] text-[14px]">
                                        {searchValue.trim()
                                            ? `No questions found matching "${searchValue}"`
                                            : selectedCategoryId === 'saved'
                                              ? 'No saved questions yet. Save your own questions here.'
                                              : 'No questions available.'}
                                    </p>
                                </div>
                            ) : (
                                displayedQuestions.map((question) => {
                                    const isEditing = editingQuestionId === question.id;
                                    const isSavedCategory = selectedCategoryId === 'saved';

                                    if (isEditing && isSavedCategory) {
                                        return (
                                            <div
                                                key={question.id}
                                                className="border-b border-[#f0f0f0] min-h-[40px] h-[40px] flex items-center justify-between gap-2 px-2 pr-4"
                                                onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <Input
                                                        value={editValue}
                                                        onChange={(e: { target: { value: string } }) => setEditValue(e.target.value)}
                                                        onPressEnter={() => handleSaveEdit(question.id)}
                                                        className="h-[32px] text-[14px]"
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="flex items-center gap-[6px]">
                                                    <div
                                                        className="cursor-pointer hover:opacity-70"
                                                        onClick={() => handleSaveEdit(question.id)}
                                                    >
                                                        <Check size={16} color="#42a531" />
                                                    </div>
                                                    <div
                                                        className="cursor-pointer hover:opacity-70"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        <XClose size={16} color="#ca3d3d" />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            key={question.id}
                                            className="border-b border-[#f0f0f0] min-h-[40px] h-[40px] flex items-center justify-between cursor-pointer hover:bg-[#FBFAFF] transition-colors px-2 pr-4"
                                            onClick={() => handleQuestionClick(question)}
                                            data-test-id={`${dataTestId}-question-${question.id}`}
                                        >
                                            <p className="flex-1 font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] m-0 truncate">
                                                {renderQuestionText(question.text)}
                                            </p>
                                            {isSavedCategory && (
                                                <div className="flex items-center gap-2 ml-2 shrink-0">
                                                    <div
                                                        className="cursor-pointer hover:opacity-70"
                                                        onClick={(e: { stopPropagation: () => void }) => handleEditClick(e, question)}
                                                    >
                                                        <EditDoc size={16} color="grey" />
                                                    </div>
                                                    <div
                                                        className="cursor-pointer hover:opacity-70"
                                                        onClick={(e: { stopPropagation: () => void }) => handleDeleteClick(e, question.id)}
                                                    >
                                                        <Trash size={16} color="grey" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    ) as React.ReactNode;
};

export default AgenticQuestionBank;
